import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  CalendarClock,
  CircuitBoard,
  Gauge,
  Radio,
  CreditCard,
  Layers,
} from 'lucide-react';
import MarketingNavbar from '@/components/marketing/MarketingNavbar';
import { useVenueContext } from '@/providers/VenueProvider';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const FEATURE_KEYS = [
  { icon: Layers, title: 'marketing.feature_booking_title', body: 'marketing.feature_booking_body' },
  { icon: CircuitBoard, title: 'marketing.feature_iot_title', body: 'marketing.feature_iot_body' },
  { icon: Gauge, title: 'marketing.feature_pricing_title', body: 'marketing.feature_pricing_body' },
  { icon: CalendarClock, title: 'marketing.feature_session_title', body: 'marketing.feature_session_body' },
  { icon: Radio, title: 'marketing.feature_dashboard_title', body: 'marketing.feature_dashboard_body' },
  { icon: CreditCard, title: 'marketing.feature_payments_title', body: 'marketing.feature_payments_body' },
] as const;

const HOW_STEPS = [
  { step: '01', titleKey: 'marketing.how_step1_title', bodyKey: 'marketing.how_step1_body' },
  { step: '02', titleKey: 'marketing.how_step2_title', bodyKey: 'marketing.how_step2_body' },
  { step: '03', titleKey: 'marketing.how_step3_title', bodyKey: 'marketing.how_step3_body' },
] as const;

const VALUE_PROP_KEYS = [
  { label: 'marketing.value_multi_sport_label', body: 'marketing.value_multi_sport_body' },
  { label: 'marketing.value_automated_label', body: 'marketing.value_automated_body' },
  { label: 'marketing.value_cloud_label', body: 'marketing.value_cloud_body' },
  { label: 'marketing.value_multi_tenant_label', body: 'marketing.value_multi_tenant_body' },
] as const;

const PREVIEW_CARDS = [
  { src: '/screenshots/dashboard.png', captionKey: 'marketing.preview_dashboard_caption' },
  { src: '/screenshots/bookings.png', captionKey: 'marketing.preview_bookings_caption' },
  { src: '/screenshots/units.png', captionKey: 'marketing.preview_units_caption' },
] as const;

function Reveal({
  children,
  as: Tag = 'section',
  className = '',
  delayMs = 0,
  ...rest
}: {
  children: React.ReactNode;
  as?: 'section' | 'div' | 'footer';
  className?: string;
  delayMs?: number;
} & React.HTMLAttributes<HTMLElement>) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const style = delayMs ? { transitionDelay: `${delayMs}ms` } : undefined;
  return (
    <Tag className={className} {...rest}>
      <div
        ref={ref}
        style={style}
        className={
          'transition-[opacity,transform] duration-700 ease-out ' +
          (isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-6')
        }
      >
        {children}
      </div>
    </Tag>
  );
}

export default function MarketingHomePage() {
  const { t } = useTranslation();
  const { currentSlug } = useVenueContext();

  return (
    <div className="min-h-screen bg-[#110D09] text-white antialiased">
      <MarketingNavbar />

      <main>
        {/* Hero */}
        <section
          aria-labelledby="hero-heading"
          className="relative overflow-hidden pt-36 pb-28 md:pt-44 md:pb-36"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(194,113,58,0.28),_transparent_60%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay [background-image:radial-gradient(#fff_1px,transparent_1px)] [background-size:3px_3px]"
          />

          <div className="relative mx-auto max-w-5xl px-4 text-center">
            <p className="mb-6 inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
              {t('marketing.eyebrow_operator_platform')}
            </p>
            <h1
              id="hero-heading"
              className="text-balance text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.02]"
            >
              {t('marketing.hero_title_prefix')}{' '}
              <span className="bg-gradient-to-br from-[#E79754] to-[#C2713A] bg-clip-text text-transparent">
                {t('marketing.hero_title_highlight')}
              </span>{' '}
              {t('marketing.hero_title_suffix')}
            </h1>
            <p className="mt-7 text-lg md:text-xl text-white/70 max-w-2xl mx-auto leading-[1.65]">
              {t('marketing.hero_subtitle')}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/demo"
                className="group inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-6 py-3 text-base font-medium text-white transition-all duration-200 hover:bg-[#a95f2f] hover:shadow-[0_0_28px_rgba(194,113,58,0.35)] hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#110D09]"
              >
                {t('marketing.cta_demo')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/venues"
                className="inline-flex items-center gap-2 rounded-md border border-white/15 px-6 py-3 text-base font-medium text-white/90 transition-colors hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#110D09]"
              >
                {t('marketing.cta_venues')}
              </Link>
            </div>
            <p className="mt-7 text-xs text-white/40 font-mono">
              {t('marketing.try_live_flow_prefix')}{' '}
              <Link
                to={`/venue/${currentSlug}/book`}
                className="underline decoration-white/20 underline-offset-4 hover:text-white/70"
              >
                /venue/{currentSlug}/book
              </Link>
            </p>
          </div>
        </section>

        {/* Value prop strip */}
        <Reveal
          aria-label="Platform highlights"
          className="border-y border-white/5 bg-[#0c0907]"
        >
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-8 px-4 py-14 md:grid-cols-4">
            {VALUE_PROP_KEYS.map((vp) => (
              <div key={vp.label} className="text-center md:text-left">
                <p className="text-xs font-semibold text-[#C2713A] uppercase tracking-[0.16em]">
                  {t(vp.label)}
                </p>
                <p className="mt-2 text-sm text-white/70 leading-[1.65]">
                  {t(vp.body)}
                </p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Feature grid */}
        <Reveal aria-labelledby="features-heading" className="mx-auto max-w-6xl px-4 py-24 md:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C2713A]">
              {t('marketing.features_eyebrow')}
            </p>
            <h2
              id="features-heading"
              className="mt-3 text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.1]"
            >
              {t('marketing.features_title')}
            </h2>
            <p className="mt-4 text-white/70 leading-[1.65]">
              {t('marketing.features_subtitle')}
            </p>
          </div>

          <FeatureGrid />
        </Reveal>

        {/* See it in action — screenshot placeholders */}
        <Reveal
          aria-labelledby="preview-heading"
          className="border-y border-white/5 bg-[#0c0907]"
        >
          <div className="mx-auto max-w-6xl px-4 py-24 md:py-28">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C2713A]">
                {t('marketing.preview_eyebrow')}
              </p>
              <h2
                id="preview-heading"
                className="mt-3 text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.1]"
              >
                {t('marketing.preview_title')}
              </h2>
              <p className="mt-4 text-white/70 leading-[1.65]">
                {t('marketing.preview_subtitle')}
              </p>
            </div>

            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {PREVIEW_CARDS.map(({ src, captionKey }) => (
                <figure
                  key={src}
                  className="group relative aspect-[16/10] overflow-hidden rounded-xl border border-[#C2713A]/15 bg-[#0c0907] transition-colors hover:border-[#C2713A]/30"
                >
                  <img
                    src={src}
                    alt={t(captionKey)}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0c0907] via-[#0c0907]/20 to-transparent"
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 p-4 font-mono text-xs uppercase tracking-[0.18em] text-white/80">
                    {t(captionKey)}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Reveal>

        {/* How it works */}
        <Reveal
          aria-labelledby="how-heading"
          className="mx-auto max-w-6xl px-4 py-24 md:py-32"
        >
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C2713A]">
              {t('marketing.how_eyebrow')}
            </p>
            <h2
              id="how-heading"
              className="mt-3 text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.1]"
            >
              {t('marketing.how_title')}
            </h2>
          </div>

          <ol className="relative mt-14 grid gap-6 md:grid-cols-3 md:gap-8">
            <div
              aria-hidden
              className="pointer-events-none absolute left-[12%] right-[12%] top-[46px] hidden h-px bg-gradient-to-r from-transparent via-[#C2713A]/25 to-transparent md:block"
            />
            {HOW_STEPS.map((step, i) => (
              <li
                key={step.step}
                className="relative rounded-xl border border-white/5 bg-[#0c0907] p-6 transition-colors duration-200 hover:border-[#C2713A]/30"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#C2713A]/30 bg-[#C2713A]/10 font-mono text-sm font-semibold text-[#C2713A]">
                  {step.step}
                </span>
                <h3 className="mt-4 text-lg font-semibold tracking-tight">
                  {t(step.titleKey)}
                </h3>
                <p className="mt-2 text-sm text-white/70 leading-[1.65]">
                  {t(step.bodyKey)}
                </p>
              </li>
            ))}
          </ol>
        </Reveal>

        {/* Early access */}
        <Reveal
          aria-labelledby="early-access-heading"
          className="border-y border-white/5 bg-[#0c0907]"
        >
          <div className="mx-auto max-w-6xl px-4 py-24 md:py-32 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C2713A]">
              {t('marketing.early_access_eyebrow')}
            </p>
            <h2
              id="early-access-heading"
              className="mt-3 text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.1]"
            >
              {t('marketing.early_access_title')}
            </h2>
            <p className="mt-4 mx-auto max-w-2xl text-white/70 leading-[1.65]">
              {t('marketing.early_access_body')}
            </p>
            <div className="mt-10">
              <Link
                to="/demo"
                className="group inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-6 py-3 text-base font-medium text-white transition-all duration-200 hover:bg-[#a95f2f] hover:shadow-[0_0_28px_rgba(194,113,58,0.35)] hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#110D09]"
              >
                {t('marketing.early_access_cta')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>

        {/* Pricing */}
        <Reveal aria-labelledby="pricing-heading" className="mx-auto max-w-4xl px-4 py-24 md:py-28 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C2713A]">
            {t('marketing.pricing_eyebrow')}
          </p>
          <h2
            id="pricing-heading"
            className="mt-3 text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.1]"
          >
            {t('marketing.pricing_title')}
          </h2>
          <p className="mt-4 mx-auto max-w-2xl text-white/70 leading-[1.65]">
            {t('marketing.pricing_body')}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/demo"
              className="group inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-6 py-3 text-base font-medium text-white transition-all duration-200 hover:bg-[#a95f2f] hover:shadow-[0_0_28px_rgba(194,113,58,0.35)] hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#110D09]"
            >
              {t('marketing.cta_demo')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/venues"
              className="text-sm text-white/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-sm"
            >
              {t('marketing.explore_marketplace')}
            </Link>
          </div>
        </Reveal>

        {/* Footer */}
        <Reveal as="footer" className="border-t border-white/5 bg-[#0c0907]">
          <div className="mx-auto max-w-6xl px-4 py-14 text-sm text-white/60">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-lg font-semibold tracking-tight text-white">RekPlay</p>
                <p className="mt-1 text-white/50 leading-[1.65]">
                  {t('marketing.footer_tagline')}
                </p>
              </div>
              <nav aria-label="Footer" className="flex flex-wrap gap-x-6 gap-y-2">
                <Link to="/" className="transition-colors hover:text-white">
                  {t('marketing.footer_home')}
                </Link>
                <Link to="/venues" className="transition-colors hover:text-white">
                  {t('marketing.footer_venues')}
                </Link>
                <Link to="/demo" className="transition-colors hover:text-white">
                  {t('marketing.footer_demo')}
                </Link>
                <Link to="/login" className="transition-colors hover:text-white">
                  {t('marketing.footer_login')}
                </Link>
              </nav>
            </div>
            <div className="mt-10 flex flex-col gap-2 border-t border-white/5 pt-6 md:flex-row md:items-center md:justify-between">
              <p className="text-white/40">{t('marketing.footer_copyright')}</p>
              <p className="text-white/40 font-mono text-xs">
                {t('marketing.footer_contact')}
              </p>
            </div>
          </div>
        </Reveal>
      </main>
    </div>
  );
}

function FeatureGrid() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.08);
  return (
    <div ref={ref} className="mt-14 grid gap-6 md:grid-cols-2">
      {FEATURE_KEYS.map((f, i) => {
        const Icon = f.icon;
        return (
          <article
            key={f.title}
            style={{ transitionDelay: `${i * 70}ms` }}
            className={
              'group rounded-xl border border-white/10 bg-white/[0.02] p-6 ' +
              'transition-[opacity,transform,border-color,box-shadow,background-color] duration-500 ease-out ' +
              'hover:-translate-y-1 hover:border-[#C2713A]/40 hover:bg-white/[0.04] ' +
              'hover:shadow-[0_10px_40px_-15px_rgba(194,113,58,0.35)] ' +
              (isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3')
            }
          >
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C2713A]/15 text-[#C2713A] transition-transform duration-300 group-hover:scale-110">
              <Icon className="h-5 w-5" aria-hidden="true" />
            </div>
            <h3 className="text-lg font-semibold tracking-tight">{t(f.title)}</h3>
            <p className="mt-2 text-sm text-white/70 leading-[1.7]">{t(f.body)}</p>
          </article>
        );
      })}
    </div>
  );
}
